import React from 'react';
import { Tooltip, Flex, Skeleton } from 'antd';
import { styled, css } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useGlobals } from '../../../globals-context';
import { changeLocation } from '../../../actions';
import EVENT_BUS_EVENTS from '../../../constants/event-bus-events';

import LogoShore from '../../../assets/images/logos/logo_shore.svg';
import LogoShoreMark from '../../../assets/images/logos/logo_shore_figurative_mark.svg';
import LogoLocalsearch from '../../../assets/images/logos/logo_localsearch.svg';
import LogoLocalsearchMark from '../../../assets/images/logos/logo_localsearch_figurative_mark.svg';

const logoIconStyles = css`
  width: 123px;
  height: 24px;
  flex-shrink: 0;
  pointer-events: none;
  transition:
    width 200ms ease-in-out,
    height 200ms ease-in-out;
`;

const logoMarkStyles = css`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  pointer-events: none;
`;

const StyledLogoShoreIcon = styled(LogoShore)`
  ${logoIconStyles}
`;

const StyledLogoShoreMark = styled(LogoShoreMark)`
  ${logoMarkStyles}
`;

const StyledLogoLocalsearchIcon = styled(LogoLocalsearch)`
  ${logoIconStyles}
`;

const StyledLogoLocalsearchMark = styled(LogoLocalsearchMark)`
  ${logoMarkStyles}
`;

const companyLogoMapping = {
  shore: {
    full: StyledLogoShoreIcon,
    mark: StyledLogoShoreMark,
  },
  localsearch: {
    full: StyledLogoLocalsearchIcon,
    mark: StyledLogoLocalsearchMark,
  },
};

type CompanyName = keyof typeof companyLogoMapping;

interface SidebarLogoProps {
  isLoading?: boolean;
  sideMenuCollapsed: boolean;
  onLogoClick?: () => void;
}

const StyledLogoHeader = styled(Flex)<{ $collapsed: boolean }>`
  height: 64px;
  padding: 0;
  cursor: pointer;
  transition: all 150ms ease-in-out;
  user-select: none;

  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }

  &:focus-visible {
    outline: 2px solid #00c2d1;
    outline-offset: -2px;
  }

  &:active {
    background: rgba(0, 0, 0, 0.04);
  }
`;

export function SidebarLogo({
  isLoading,
  sideMenuCollapsed,
  onLogoClick,
}: SidebarLogoProps) {
  const dispatch = useDispatch();
  const { trackMethod, eventBus } = useGlobals() as any;

  const currentMerchant = useSelector((state: any) => state.currentMerchant);

  const whiteLabel = (
    !isLoading && currentMerchant?.whiteLabel
      ? currentMerchant.whiteLabel
      : 'shore'
  ) as CompanyName;

  const companyLogos =
    companyLogoMapping?.[whiteLabel] || companyLogoMapping.shore;
  const { full: StyledLogoIcon, mark: StyledLogoMark } = companyLogos;

  const handleOnClick = () => {
    if (onLogoClick) {
      onLogoClick();
    } else {
      const route = '/calendar';
      dispatch(changeLocation(route));
      trackMethod('side_menu_logo_clicked', {
        platform: 'booking',
        location: 'side_menu',
      });
      eventBus.emit(EVENT_BUS_EVENTS.NAVIGATE, { payload: { route } });
    }
  };

  const handleOnKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOnClick();
      trackMethod('side_menu_logo_clicked', {
        platform: 'booking',
        location: 'side_menu',
      });
    }
  };

  const logoContent = (
    <StyledLogoHeader
      align="center"
      justify="center"
      $collapsed={sideMenuCollapsed}
      onClick={handleOnClick}
      onKeyDown={handleOnKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`${whiteLabel} - Go to home`}
      style={
        isLoading
          ? { cursor: 'default', pointerEvents: 'none' }
          : { cursor: 'pointer' }
      }
    >
      {isLoading ? (
        <Skeleton.Input
          active
          size="small"
          style={{ width: sideMenuCollapsed ? 13 : 153, height: 41 }}
        />
      ) : sideMenuCollapsed ? (
        <StyledLogoMark />
      ) : (
        <StyledLogoIcon />
      )}
    </StyledLogoHeader>
  );

  if (sideMenuCollapsed) {
    return (
      <Tooltip
        title={isLoading ? 'Loading...' : whiteLabel.toLowerCase()}
        placement="right"
      >
        {logoContent}
      </Tooltip>
    );
  }

  return logoContent;
}
